import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Home from './Home';
import Signup from './Signup';
import Login from './Login';
import Logout from './Logout';
import Chat from './Chat';
import './App.css';

function App() {
  const [userId, setUserId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const checkAuth = async () => {
    // Check if the user has just logged out
    const justLoggedOut = localStorage.getItem('justLoggedOut');
    if (justLoggedOut === 'true') {
      setIsAuthenticated(false);
      setUserId(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/me', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setUserId(data.userId);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUserId(null);
      }
    } catch (error) {
      if (error.name !== 'TypeError' && error.message !== 'Failed to fetch') {
        console.error('Unexpected error checking auth:', error);
      }
      setIsAuthenticated(false);
      setUserId(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = useCallback(async (retryCount = 0, maxRetries = 3) => {
    // Check if the user has just logged out
    const justLoggedOut = localStorage.getItem('justLoggedOut');
    if (justLoggedOut === 'true') {
      setIsAuthenticated(false);
      setUserId(null);
      return false;
    }

    try {
      const response = await fetch('/api/refresh-token', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        console.log('Token refreshed successfully');
        setUserId(data.userId);
        return true;
      } else {
        console.warn('Token refresh failed:', data.message);
        if (data.message === 'Invalid or expired token' || data.message === 'User not found') {
          setIsAuthenticated(false);
          setUserId(null);
          return false;
        }
        if (retryCount < maxRetries) {
          console.log(`Retrying token refresh, attempt ${retryCount + 1}/${maxRetries}`);
          await new Promise(resolve => setTimeout(resolve, 5000));
          return await refreshToken(retryCount + 1, maxRetries);
        } else {
          console.error('Max retries reached for token refresh. Not logging out to keep user on page.');
          return false;
        }
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      if (retryCount < maxRetries) {
        console.log(`Retrying token refresh, attempt ${retryCount + 1}/${maxRetries}`);
        await new Promise(resolve => setTimeout(resolve, 5000));
        return await refreshToken(retryCount + 1, maxRetries);
      } else {
        console.error('Max retries reached for token refresh. Not logging out to keep user on page.');
        return false;
      }
    }
  }, [setUserId, setIsAuthenticated]);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        refreshToken();
      }, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, refreshToken]);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (location.pathname === '/' && !loading) {
      checkAuth();
    }
  }, [location.pathname, loading]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header isAuthenticated={isAuthenticated} />
      <div className="main">
        {message && <p className="notification-message">{message}</p>}
        <Routes>
          <Route path="/" element={<Home userId={userId} />} />
          <Route
            path="/signup"
            element={
              !isAuthenticated ? (
                <Signup setUserId={setUserId} setMessage={setMessage} setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/chat" />
              )
            }
          />
          <Route
            path="/login"
            element={
              !isAuthenticated ? (
                <Login setUserId={setUserId} setMessage={setMessage} setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/chat" />
              )
            }
          />
          <Route
            path="/chat"
            element={
              isAuthenticated ? (
                <Chat userId={userId} setIsAuthenticated={setIsAuthenticated} setUserId={setUserId} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/logout"
            element={
              isAuthenticated ? (
                <Logout setUserId={setUserId} setMessage={setMessage} setIsAuthenticated={setIsAuthenticated} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="*" element={<Navigate to={isAuthenticated ? '/chat' : '/login'} />} />
        </Routes>
      </div>
      <footer className="footer">
        <p>Cloud Top Chat © 2025</p>
      </footer>
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWrapper;